import React, {useEffect} from 'react'
import {odxIconSets} from '../../constants'
import OdxIcon from '../Odx-Icon'
import {transformOdxLayoutRef} from '../../odxLayoutRef'

interface OdxModalProps {
    size: 'xsmall' | 'small' | 'medium' | 'large'
    title: string
    onClose?: () => void
    children: React.ReactNode
    footerButtons: React.ReactNode[]
    isOpen: boolean
    variant: 'default' | 'sidesheet'
    imgSrcInHeader?: string
    onBackButtonClick?: () => void,
    zIndex?: number
    id?: string
}

/**
 * Modal component. Is used to display a modal popup. Can be used as sidesheet or default modal.
 * @param size The size of the modal. Can be 'xsmall', 'small', 'medium' or 'large'.
 * @param variant Can be 'default' or 'sidesheet'.
 * @param title The title of the modal.
 * @param onClose Function to call when the modal is closed.
 * @param children The children to display in the modal content.
 * @param footerButtons The buttons to display in the modal footer. Each button is from type OdxButton.
 * @param isOpen Whether the modal is open.
 * @param imgSrcInHeader Src for the ODX Avatar in the header
 * @param onBackButtonClick Function to call when the back button is clicked, optional
 * @constructor
 */
const OdxModal: React.FC<OdxModalProps> = ({
                                               size,
                                               variant,
                                               title,
                                               onClose,
                                               children,
                                               footerButtons,
                                               isOpen,
                                               imgSrcInHeader,
                                               onBackButtonClick,
                                               zIndex,
                                               id
                                           }) => {
    useEffect(() => {
        transformOdxLayoutRef(containerRef.current)
    }, [isOpen, children])

    const containerRef = React.createRef<HTMLDivElement>()

    const GetHeaderWithImage = () => {
        return (
            <>
                <div className='odx-area-header__container' id={id}>
                    <div className='odx-area-header__inner items-center'>
                        <div className={'items-center flex'} data-odxLayout={'flex vertical-center'}>
                            <div className={"!w-[64px] !h-[64px]"}>
                                <odx-avatar
                                    size="xxl"
                                    shape="circle"
                                    variant="ghost"
                                    className="flex items-center justify-center !w-full !h-full !mt-0"
                                    style={{"--size": "64px"} as React.CSSProperties}
                                >
                                    {imgSrcInHeader ? (
                                        <img
                                            src={imgSrcInHeader}
                                            alt={`${title} icon`}
                                            className="object-cover !w-[70%] !h-[70%]"
                                        />
                                    ) : (
                                        <odx-icon name="core::folder" size="xxl"></odx-icon>
                                    )}
                                </odx-avatar>
                            </div>
                            <h1 id="modalPopUpHeaderLabel" className='odx-area-header__title !font-medium !text-4xl text-wrap !flex-auto'>
                                {title}
                            </h1>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    const GetHeader = () => {
        return (
            <div className='odx-area-header__container'>
                <div className='odx-area-header__inner'>
                    <div className='odx-area-header__title !text-3xl'>
                        <h1>{title}</h1>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <dialog
            className={`odx-modal--${size} odx-modal--${variant} odx-modal mx-auto ${
                variant === "sidesheet"
                    ? "top-0 !h-fit"
                    : "![-webkit-backdrop-filter:blur(var(--odx-v-backdrop-blur))] ![backdrop-filter:blur(var(--odx-v-backdrop-blur))] !bg-[var(--odx-c-backdrop-dark)] !shadow-[0_2px_6px_0_rgba(0,2,5,0.40)]"
            }`}
            id='modal-sidesheet-component'
            aria-labelledby='modal-sidesheet-component-title'
            role='dialog'
            open={isOpen}
            style={{zIndex: zIndex ? zIndex : 10}}
        >
            <div id="modalPopUpContent" className='odx-modal__container' ref={containerRef}>
                <div className='odx-modal__header' data-odxLayout={"flex vertical-center"}>
                    {onBackButtonClick && (
                        <odx-icon-button
                            id={"OdxModal_BackBtn"}
                            variant="ghost"
                            icon={`${odxIconSets.core}::arrow-left`}
                            size="lg"
                            className="odx-navigation-back p-0"
                            onClick={onBackButtonClick}
                        />
                    )}
                    <div className='odx-area-header odx-area-header--small'>
                        {imgSrcInHeader ? <GetHeaderWithImage/> : <GetHeader/>}
                    </div>
                    <div className='odx-modal__dismiss odx-action-group !pr-0 !mr-0'>
                        {onClose && (
                            <odx-icon-button
                                id={"OdxModalCloseButton"}
                                variant="ghost"
                                icon={`${odxIconSets.core}::close`}
                                size="sm"
                                onClick={onClose}
                            />
                        )}
                    </div>
                </div>
                <div id={"modalPopUpContentChildren_" + id} className='odx-modal__content text-wrap break-words'>
                    {children}
                </div>
                <div className='odx-modal__footer'>
                    <div className='odx-modal__footer'>
                        {footerButtons.map((button, index) =>
                            button ? <div key={index}>{button}</div> : null
                        )}
                    </div>
                </div>
            </div>
        </dialog>
    )
}

export default OdxModal
